import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, Lock, User, AlertCircle, Gift, Eye, EyeOff, ArrowLeft, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { SyncLogoWithText } from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inner component that uses reCAPTCHA
const AuthPageInner = () => {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const modeParam = searchParams.get('mode');
  
  // Default to register if ref code provided, or if mode=register
  const [isLogin, setIsLogin] = useState(modeParam !== 'register' && !refCode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
    referralCode: refCode || ''
  });
  const [referralInfo, setReferralInfo] = useState(null);
  
  // Email verification state
  const [emailVerificationEnabled, setEmailVerificationEnabled] = useState(false);
  const [verificationStep, setVerificationStep] = useState('form'); // 'form' | 'verify'
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [codeExpiresIn, setCodeExpiresIn] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const codeInputRefs = useRef([]);

  const { login, loginWithGoogleSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';
  const authError = location.state?.error; // Error from Google OAuth callback
  
  // reCAPTCHA hook
  const { executeRecaptcha } = useGoogleReCaptcha();
  
  // Check if email verification is enabled
  useEffect(() => {
    const checkEmailConfig = async () => {
      try {
        const response = await axios.get(`${API}/auth/email-config`);
        setEmailVerificationEnabled(response.data.enabled);
      } catch (e) {
        console.warn('Could not fetch email config:', e);
        setEmailVerificationEnabled(false);
      }
    };
    checkEmailConfig();
  }, []);

  // Show error from Google OAuth if present
  useEffect(() => {
    if (authError) {
      setError(authError);
      toast.error(authError);
    }
  }, [authError]);

  // Validate referral code on mount
  useEffect(() => {
    if (refCode) {
      validateReferralCode(refCode);
    }
  }, [refCode]);
  
  // Countdown timer for code expiry
  useEffect(() => {
    if (codeExpiresIn > 0) {
      const timer = setInterval(() => {
        setCodeExpiresIn(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [codeExpiresIn]);

  const validateReferralCode = async (code) => {
    try {
      const response = await axios.get(`${API}/referrals/validate/${code}`);
      if (response.data.valid) {
        setReferralInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to validate referral code:', error);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    
    // Validate referral code when changed
    if (e.target.name === 'referralCode' && e.target.value.length >= 6) {
      validateReferralCode(e.target.value);
    } else if (e.target.name === 'referralCode') {
      setReferralInfo(null);
    }
  };
  
  // Handle verification code input
  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setError('');
    
    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };
  
  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };
  
  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newCode = [...verificationCode];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newCode[i] = pastedData[i];
      }
      setVerificationCode(newCode);
      // Focus the next empty input or last input
      const nextEmptyIndex = newCode.findIndex(c => !c);
      if (nextEmptyIndex !== -1) {
        codeInputRefs.current[nextEmptyIndex]?.focus();
      } else {
        codeInputRefs.current[5]?.focus();
      }
    }
  };
  
  // Send verification code (Step 1 of email-verified registration)
  const sendVerificationCode = async () => {
    setError('');
    setLoading(true);
    
    try {
      // Get reCAPTCHA token if available
      let recaptchaToken = null;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('register');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA execution failed:', recaptchaError);
        }
      }
      
      const response = await axios.post(`${API}/auth/send-verification-code`, {
        email: formData.email,
        password: formData.password,
        name: formData.name || formData.email.split('@')[0],
        referral_code: formData.referralCode || null,
        recaptcha_token: recaptchaToken
      });
      
      // Move to verification step
      setVerificationStep('verify');
      setCodeExpiresIn(response.data.expires_in_minutes * 60);
      setVerificationCode(['', '', '', '', '', '']);
      toast.success('Verification code sent to your email!');
      
      // Focus first code input after a short delay
      setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 100);
      
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send verification code';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  // Verify code and complete registration (Step 2)
  const verifyCodeAndRegister = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/verify-email-and-register`, {
        email: formData.email,
        code: code,
        password: formData.password,
        name: formData.name || formData.email.split('@')[0],
        referral_code: formData.referralCode || null
      });
      
      // Successfully registered - log in
      const { access_token, user: userData } = response.data;
      await loginWithGoogleSession(access_token, userData);
      
      if (referralInfo) {
        toast.success(`Account created! You got ${referralInfo.bonus_credits} bonus credits!`);
      } else {
        toast.success('Email verified! Welcome aboard!');
      }
      
      navigate(from, { replace: true });
      
    } catch (err) {
      const message = err.response?.data?.detail || 'Verification failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  // Resend verification code
  const resendCode = async () => {
    setResendDisabled(true);
    await sendVerificationCode();
    
    // Disable resend for 60 seconds
    setTimeout(() => {
      setResendDisabled(false);
    }, 60000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate(from, { replace: true });
      } else {
        // Validation for registration
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.password.length < 8) {
          setError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        
        // If email verification is enabled, go through the 2-step flow
        if (emailVerificationEnabled) {
          setLoading(false);
          await sendVerificationCode();
          return;
        }
        
        // Legacy registration (no email verification)
        let recaptchaToken = null;
        if (executeRecaptcha) {
          try {
            recaptchaToken = await executeRecaptcha('register');
          } catch (recaptchaError) {
            console.warn('reCAPTCHA execution failed:', recaptchaError);
          }
        }
        
        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name || formData.email.split('@')[0],
          referral_code: formData.referralCode || null,
          recaptcha_token: recaptchaToken
        });
        
        const { access_token, user: userData } = response.data;
        await loginWithGoogleSession(access_token, userData);
        
        if (referralInfo) {
          toast.success(`Account created! You got ${referralInfo.bonus_credits} bonus credits!`);
        } else {
          toast.success('Account created! Welcome aboard!');
        }
        navigate(from, { replace: true });
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'An error occurred';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Uses Emergent-managed Google Auth - redirects to Emergent auth server
    // After Google auth, user returns to current origin with session_id in hash
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
        
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <SyncLogoWithText variant="dark" size="large" />
          </div>
          <p className="text-gray-400 mt-2">Automated Crypto Trading Bot</p>
        </div>

        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          {/* Verification Code Step */}
          {verificationStep === 'verify' && !isLogin ? (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-white text-center">
                  Verify Your Email
                </CardTitle>
                <CardDescription className="text-gray-400 text-center">
                  We sent a 6-digit code to <span className="text-[#5A9BD5]">{formData.email}</span>
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                {error && (
                  <Alert className="mb-4 bg-red-500/20 border-red-500/50 text-red-100">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-6">
                  {/* Code Input */}
                  <div className="flex justify-center gap-2">
                    {verificationCode.map((digit, index) => (
                      <Input
                        key={index}
                        ref={el => codeInputRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        onPaste={index === 0 ? handleCodePaste : undefined}
                        className="w-12 h-14 text-center text-2xl font-bold bg-white/5 border-white/20 text-white focus:border-[#5A9BD5]"
                        data-testid={`verification-code-${index}`}
                      />
                    ))}
                  </div>
                  
                  {/* Timer */}
                  {codeExpiresIn > 0 && (
                    <p className="text-center text-sm text-gray-400">
                      Code expires in{' '}
                      <span className="text-[#5A9BD5] font-semibold">
                        {Math.floor(codeExpiresIn / 60)}:{(codeExpiresIn % 60).toString().padStart(2, '0')}
                      </span>
                    </p>
                  )}
                  
                  {/* Verify Button */}
                  <Button
                    onClick={verifyCodeAndRegister}
                    disabled={loading || verificationCode.join('').length !== 6}
                    className="w-full bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white"
                    data-testid="verify-code-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Verify & Create Account
                      </>
                    )}
                  </Button>
                  
                  {/* Resend Code */}
                  <div className="text-center">
                    <p className="text-sm text-gray-400 mb-2">Didn't receive the code?</p>
                    <Button
                      variant="ghost"
                      onClick={resendCode}
                      disabled={resendDisabled || loading}
                      className="text-[#5A9BD5] hover:text-[#6AABF8]"
                      data-testid="resend-code-button"
                    >
                      <RefreshCw className={`mr-2 h-4 w-4 ${resendDisabled ? 'animate-spin' : ''}`} />
                      {resendDisabled ? 'Please wait...' : 'Resend Code'}
                    </Button>
                  </div>
                  
                  {/* Back Button */}
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setVerificationStep('form');
                      setVerificationCode(['', '', '', '', '', '']);
                      setError('');
                    }}
                    className="w-full text-gray-400 hover:text-white"
                    data-testid="back-to-form-button"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Registration
                  </Button>
                </div>
              </CardContent>
            </>
          ) : (
            <>
              {/* Standard Login/Register Form */}
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-white text-center">
                  {isLogin ? 'Welcome Back' : 'Create Account'}
                </CardTitle>
                <CardDescription className="text-gray-400 text-center">
                  {isLogin 
                    ? 'Sign in to access your dashboard'
                    : 'Start trading across multiple exchanges'
                  }
                </CardDescription>
              </CardHeader>

              <CardContent>
                {error && (
                  <Alert className="mb-4 bg-red-500/20 border-red-500/50 text-red-100">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                    <Input
                      id="name"
                      name="name"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#5A9BD5]"
                      data-testid="auth-name-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#5A9BD5]"
                    data-testid="auth-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#5A9BD5]"
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-gray-500 hover:text-white transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#5A9BD5]"
                      data-testid="auth-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-3 text-gray-500 hover:text-white transition-colors"
                      data-testid="toggle-confirm-password-visibility"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="referralCode" className="text-gray-300">Referral Code (Optional)</Label>
                  <div className="relative">
                    <Gift className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                    <Input
                      id="referralCode"
                      name="referralCode"
                      placeholder="Enter referral code"
                      value={formData.referralCode}
                      onChange={handleChange}
                      className={`pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#5A9BD5] ${referralInfo?.valid ? 'border-green-500' : ''}`}
                      data-testid="auth-referral-input"
                    />
                  </div>
                  {referralInfo?.valid && (
                    <p className="text-sm text-green-400 flex items-center gap-1">
                      <Gift className="h-3 w-3" />
                      +{referralInfo.bonus_credits} bonus credits from {referralInfo.referrer_name}!
                    </p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white"
                data-testid="auth-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-transparent text-gray-500">or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10"
              data-testid="google-login-button"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </Button>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            {isLogin && (
              <div className="text-center">
                <Link 
                  to="/forgot-password"
                  className="text-sm text-[#5A9BD5] hover:text-[#6AABF8] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            )}
            
            <div className="text-center text-sm text-gray-400">
              {isLogin ? (
                <>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLogin(false);
                      setError('');
                      setVerificationStep('form');
                    }}
                    className="text-[#5A9BD5] hover:text-[#6AABF8] font-semibold"
                    data-testid="switch-to-register"
                  >
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLogin(true);
                      setError('');
                      setVerificationStep('form');
                    }}
                    className="text-[#5A9BD5] hover:text-[#6AABF8] font-semibold"
                    data-testid="switch-to-login"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>

            {!isLogin && (
              <p className="text-xs text-gray-500 text-center">
                New users get <span className="text-[#5A9BD5] font-semibold">100 free credits</span> to start trading!
                {referralInfo?.valid && (
                  <span className="block mt-1">
                    Plus <span className="text-green-400 font-semibold">+{referralInfo.bonus_credits}</span> referral bonus!
                  </span>
                )}
              </p>
            )}
          </CardFooter>
            </>
          )}
        </Card>

        <p className="mt-6 text-center text-sm text-gray-500">
          By continuing, you agree to our{' '}
          <a href="#" className="underline hover:text-gray-300">Terms of Service</a>
          {' '}and{' '}
          <a href="#" className="underline hover:text-gray-300">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

// Wrapper component that provides reCAPTCHA context
const AuthPage = () => {
  const [recaptchaConfig, setRecaptchaConfig] = useState({ enabled: false, site_key: '' });
  
  useEffect(() => {
    // Fetch reCAPTCHA config from backend
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/auth/recaptcha-config`);
        setRecaptchaConfig(response.data);
      } catch (e) {
        console.warn('Could not fetch reCAPTCHA config:', e);
      }
    };
    fetchConfig();
  }, []);
  
  // If reCAPTCHA is enabled and we have a site key, wrap with provider
  if (recaptchaConfig.enabled && recaptchaConfig.site_key) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={recaptchaConfig.site_key}>
        <AuthPageInner />
      </GoogleReCaptchaProvider>
    );
  }
  
  // Otherwise render without provider
  return <AuthPageInner />;
};

export default AuthPage;
